import intersection from 'lodash/fp/intersection';
import isEmpty from 'lodash/fp/isEmpty';
import { stringify } from 'querystring';
import { useSelector } from 'react-redux';
import { generatePath, Navigate, PathRouteProps, Route, Routes, RoutesProps, useLocation } from 'react-router-dom';
import { PermissionProvider as PdpProvider } from '@katalon-studio/pdp-react';
import React from 'react';
import { Layout } from '../layout';
import { AccountRole, RouteVersion } from '../models';
import Forbidden from '../pages/forbidden';
import Login from '../pages/login';
import Logout from '../pages/logout';
import NotFoundComponent from '../pages/not-found';
import { fromAuth } from '../store';
import { RouteType } from './routeType';
import RouteWithPageTitle from './RouteWithPageTitle';
import { QueryDictionary } from './useNavigate';
import { PermissionProvider } from '../permission';
import { PermissionAction, ResourcePolicy } from '../models/permission';
import { useQuery } from './useQuery';
import { resetAnalyticsIdentity } from '../analytics/reset';
import ProtectedRoute from './ProtectedRoute';
import { subscription } from '../layout/routes';

export type RouteConfig = PathRouteProps & {
  path: string;
  icon?: React.ReactNode,
  redirect?: string;
  requireAuth?: boolean;
  roles?: string[];
  pageTitle?: string;
  accessibleRoles?: AccountRole[];
  isRootOrAdminAccessible?: boolean;
  type?: RouteType;
  resource?: ResourcePolicy;
  actions?: PermissionAction[];
  accountRequired?: boolean;
  showInMenu?: boolean;
  menuGroup?: 'manage' | 'billing';
  showSidebar?: boolean;
  showNavbar?: boolean;
  version?: RouteVersion;
  replacePath?: string;
  queryParams?: string;
};

const renderRoutes = (
  routes: RouteConfig[],
  switchProps?: RoutesProps,
) => {
  const location = useLocation();
  const user = useSelector(fromAuth.selectUser);
  const routeList = [
    ...routes,
    {
      path: '*',
      redirect: '/notfound',
      pageTitle: 'app.page.title.notfound',
    },
  ].map((
    {
      redirect,
      requireAuth,
      path,
      roles,
      accessibleRoles,
      isRootOrAdminAccessible,
      type,
      element,
      queryParams,
      ...routeProps
    },
  ) => {
    if (redirect) {
      const params = location.search
        ? `${location.search}&${queryParams ?? ''}`
        : queryParams ? `?${queryParams}` : '';
      return (
        <Route
          key={path}
          path={path}
          element={(
            <Navigate
              to={`${redirect}${params}`}
              replace
              {...routeProps}
            />
          )}
        />
      );
    }
    if (requireAuth) {
      // Check if user roles match at least 1 route role
      if (!isEmpty(roles) && isEmpty(intersection(user?.roles, roles))) {
        return (
          <Route
            key={path}
            path={path}
            element={(
              <Navigate to={forbidden.path} replace />
            )}
          />
        );
      }

      return (
        <Route
          key={path}
          path={path}
          element={(
            <ProtectedRoute
              key={path}
              path={path}
              element={element}
              {...routeProps}
            />
          )}
          {...routeProps}
        />
      );
    }
    return (
      <Route
        key={path}
        path={path}
        element={<RouteWithPageTitle element={element} />}
        {...routeProps}
      />
    );
  });
  return (
    <Routes {...switchProps}>
      {routeList}
    </Routes>
  );
};

export const AppRoutes = (
  { routes, switchProps }: { routes: RouteConfig[]; switchProps?: RoutesProps; },
) => {
  const { get } = useQuery();
  const token = useSelector(fromAuth.selectToken);
  const selectedAccountId = get('accountId');
  return (
    <PdpProvider
      pdpServiceUrl={process.env.REACT_APP_PDP_SERVICE_URL || ''}
      accessToken={token?.jwt}
    >
      <PermissionProvider env={process.env.NODE_ENV} accountId={selectedAccountId}>
        {renderRoutes(routes, switchProps)}
      </PermissionProvider>
    </PdpProvider>
  );
};

// we're trying to make the sub routes generic enough to not knowing their ancestors
// so in case a route has never been rendered,
// it's correct to assume that it'll be located in root path
// though it's not what we always expect, but it'll work most of the time
export const resolvePath = <T extends any>(
  route: RouteConfig,
  // eslint-disable-next-line default-param-last
  params: Parameters<typeof generatePath>[1] = {},
  queries?: QueryDictionary<T>,
  shouldAddBasename = true,
) => Array.of(
    generatePath(shouldAddBasename
      ? generateRelativePathWhenNotRouterLink(route.path)
      : generateRelativePath(route.path), params),
    queries ? stringify(queries) : '',
  ).filter(Boolean).join('?');

const generateRelativePath = (path: string) => {
  const currentPathArray = window.location.pathname.split('/').filter(Boolean);
  if (currentPathArray.length - 1 > 0) {
    const relativePath = '../'.repeat(currentPathArray.length - 1);
    return `${relativePath}..${path}`;
  }
  return `..${path}`;
};

const generateRelativePathWhenNotRouterLink = (path: string) => {
  const currentPathArray = window.location.pathname.split('/').filter(Boolean);
  if (currentPathArray.length - 1 > 0) {
    const relativePath = '../'.repeat(currentPathArray.length - 1);
    return `${relativePath}..${path.startsWith(process.env.PUBLIC_URL) ? path : (process.env.PUBLIC_URL + path)}`;
  }
  if (path.startsWith(process.env.PUBLIC_URL)) {
    return `..${path}`;
  }
  return `..${process.env.PUBLIC_URL}${path}`;
};

export const login: RouteConfig = {
  path: '/login',
  element: <Login />,
};

export const notFound: RouteConfig = {
  path: '/notfound',
  element: <NotFoundComponent />,
  requireAuth: true,
};

export const logout: RouteConfig = {
  path: '/logout',
  element: <Logout />,
};

/** Absolute URL of the app root, honouring PUBLIC_URL (e.g. `https://host/sub-path`). */
export const getAppBaseUrl = () => `${window.location.origin}${process.env.PUBLIC_URL ?? ''}`;

/**
 * Sign out entry point for in-app UI (sidebar, account selection, ...).
 * A full navigation to the Logout page unmounts the SPA first, so the route guards
 * (`useAuthenticate`, `useAccountAuthenticate`) cannot react to the auth state being
 * cleared and bounce the user to /login or /notfound mid-logout.
 */
export const redirectToLogoutPage = () => {
  // Reset Segment/PostHog identity here, while analytics.js is loaded and the call is
  // synchronous. The Logout page repeats it, but there it is best-effort: that page is a
  // fresh load whose snippet stub only queues `reset` until the SDK arrives, and the
  // Keycloak redirect usually wins that race.
  resetAnalyticsIdentity();
  window.location.assign(`${process.env.PUBLIC_URL ?? ''}${logout.path}`);
};

export const forbidden: RouteConfig = {
  path: '/forbidden',
  element: <Forbidden />,
  requireAuth: false,
  pageTitle: 'app.page.title.forbidden',
};

export const homePage: RouteConfig = {
  path: '/',
  redirect: subscription.path,
};

const routes: RouteConfig[] = [
  login,
  logout,
  notFound,
  forbidden,
  homePage,
  {
    path: '/*',
    element: <Layout />,
    requireAuth: true,
  },
];

export * from './useNavigate';
export * from './useQuery';
export * from './usePortalAuthenticate';
export * from './usePortalContext';
export default routes;
